import { useEffect, useState, useCallback } from 'react';
import { useEditProfileForm } from '../../hooks/useEditProfileForm';
import AvatarUploadControl from '../AvatarUploadControl/AvatarUploadControl';
import styles from './EditProfileModal.module.scss';

const DISPLAY_NAME_MAX = 30;
const BIO_MAX = 300;

export interface EditProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  userData: {
    username: string;
    bio: string;
    avatarUrl?: string;
  };
  onSuccess?: (updatedUser: any) => void;
}

export default function EditProfileModal({
  isOpen,
  onClose,
  userData,
  onSuccess,
}: EditProfileModalProps) {
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const [saving, setSaving] = useState(false);

  const handleSuccess = useCallback(
    (updatedUser: any) => {
      onSuccess?.(updatedUser);
      // Close immediately — the loader stays visible until the modal unmounts
      onClose();
    },
    [onClose, onSuccess]
  );

  const {
    displayName,
    bio,
    avatarPreview,
    errors,
    isSubmitting,
    bioRemaining,
    handleDisplayNameChange,
    handleBioChange,
    handleAvatarSelect,
    handlePresetAvatarSelect,
    handleSubmit,
  } = useEditProfileForm(userData, handleSuccess, isOpen);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Auto-dismiss toast after 5 seconds
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(timer);
  }, [toast]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setSaving(false);
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Show error toast when general error occurs (timeout, server errors)
  useEffect(() => {
    if (errors.general) {
      setToast({ type: 'error', message: errors.general });
    }
  }, [errors.general]);

  if (!isOpen) return null;

  const handleSaveClick = async () => {
    setSaving(true);
    const success = await handleSubmit();
    if (!success) {
      setSaving(false);
    }
    // On success, modal closes immediately — saving stays true (loader visible until unmount)
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const hasAvatar = Boolean(avatarPreview);

  return (
    <>
      <div
        className={styles.backdrop}
        onClick={handleBackdropClick}
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-profile-title"
      >
        <div className={styles.modal}>
          {/* Full-modal loading overlay */}
          {saving && (
            <div className={styles.loadingOverlay}>
              <div className={styles.loadingSpinner} />
              <p className={styles.loadingText}>Saving...</p>
            </div>
          )}

          {/* Header */}
          <div className={styles.header}>
            <h2 id="edit-profile-title" className={styles.title}>
              Edit Profile
            </h2>
            <button
              type="button"
              className={styles.closeBtn}
              onClick={onClose}
              aria-label="Close"
            >
              ✕
            </button>
          </div>

          {/* Body */}
          <div className={styles.body}>
            {/* Avatar */}
            <AvatarUploadControl
              avatarPreview={avatarPreview}
              username={displayName || userData.username}
              onSelect={handleAvatarSelect}
              onPresetSelect={handlePresetAvatarSelect}
              error={errors.avatar}
              hasAvatar={hasAvatar}
            />

            {/* Display Name */}
            <div className={styles.fieldGroup}>
              <div className={styles.labelRow}>
                <label htmlFor="edit-display-name" className={styles.label}>
                  Display Name
                </label>
                <span
                  className={`${styles.charCount} ${
                    displayName.length > DISPLAY_NAME_MAX ? styles.charCountWarn : ''
                  }`}
                >
                  {displayName.length}/{DISPLAY_NAME_MAX}
                </span>
              </div>
              <input
                id="edit-display-name"
                type="text"
                className={`${styles.input} ${errors.displayName ? styles.inputError : ''}`}
                value={displayName}
                onChange={(e) => handleDisplayNameChange(e.target.value)}
                maxLength={DISPLAY_NAME_MAX}
                placeholder="Enter display name"
                autoComplete="off"
              />
              {errors.displayName && (
                <p className={styles.fieldError} role="alert">
                  {errors.displayName}
                </p>
              )}
            </div>

            {/* Bio */}
            <div className={styles.fieldGroup}>
              <div className={styles.labelRow}>
                <label htmlFor="edit-bio" className={styles.label}>
                  Bio
                </label>
                <span
                  className={`${styles.charCount} ${
                    bioRemaining < 0 ? styles.charCountWarn : ''
                  }`}
                >
                  {bioRemaining} remaining
                </span>
              </div>
              <textarea
                id="edit-bio"
                className={`${styles.textarea} ${errors.bio ? styles.inputError : ''}`}
                value={bio}
                onChange={(e) => handleBioChange(e.target.value)}
                maxLength={BIO_MAX}
                placeholder="Tell others about yourself"
                rows={4}
              />
              {errors.bio && (
                <p className={styles.fieldError} role="alert">
                  {errors.bio}
                </p>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className={styles.footer}>
            <button
              type="button"
              className={styles.cancelBtn}
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="button"
              className={styles.saveBtn}
              onClick={handleSaveClick}
              disabled={isSubmitting}
            >
              {isSubmitting && <span className={styles.spinner} />}
              {isSubmitting ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>

      {/* Toast notification */}
      {toast && (
        <div
          className={`${styles.toast} ${
            toast.type === 'success' ? styles.toastSuccess : styles.toastError
          }`}
          role="status"
          aria-live="polite"
        >
          {toast.message}
        </div>
      )}
    </>
  );
}
